/**
 * useUploadVideo Hook
 *
 * Uploads a profile video through the NATIVE pipeline (same machinery as
 * ritual videos): probe duration -> mint a GCS signed URL -> resumable PUT
 * to GCS -> confirm (which enqueues the branded-intro + Cloudflare Stream
 * transcode). Returns the created row; it's `pending` until the worker
 * finishes (the list query polls for `ready`).
 */

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { probeVideoDuration, uploadResumable } from "@/features/rituals/utils/gcsResumableUpload"
import {
  type ConfirmUploadResponse,
  confirmProfileVideoUpload,
  requestProfileVideoUploadUrl,
} from "../services/profileVideo.service"

export interface UploadProfileVideoArgs {
  userId: string
  file: File
  onProgress?: (uploadedBytes: number, totalBytes: number) => void
}

async function uploadProfileVideo(args: UploadProfileVideoArgs): Promise<ConfirmUploadResponse> {
  const { userId, file, onProgress } = args

  // Client-side duration probe (best-effort; the worker re-checks via ffprobe).
  // 0 = "couldn't probe" -- the server treats it as unknown, not a violation.
  let durationSeconds = 0
  try {
    const probed = await probeVideoDuration(file)
    if (probed > 0) durationSeconds = Math.round(probed)
  } catch {
    durationSeconds = 0
  }

  const upload = await requestProfileVideoUploadUrl(userId, {
    filename: file.name,
    contentType: file.type || "video/mp4",
    sizeBytes: file.size,
    durationSeconds,
  })

  await uploadResumable({
    signedUrl: upload.signedUrl,
    file,
    contentType: upload.contentType,
    onProgress,
  })

  return confirmProfileVideoUpload(userId, {
    gcsPath: upload.gcsPath,
    title: file.name,
  })
}

export function useUploadVideo() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (args: UploadProfileVideoArgs) => uploadProfileVideo(args),
    onSuccess: async (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ["video", variables.userId] })
    },
  })
}
